import type { FranchiseJobGroup, JobItem } from "./types";

import Location from "@homeinstead/icons/Location";
import { ChevronRight } from "lucide-react";
import { JobCard } from "./JobCard";

function buildFallbackJobItem(
  group: Extract<FranchiseJobGroup, { kind: "fallback" }>,
): JobItem {
  // Defensive default when the orchestrator emits an empty label
  // (label is constructed from the franchise name, so this is mostly belt-and-suspenders).
  const title = group.label || `See open jobs at ${group.name}`;
  return {
    id: `fallback-${group.franchiseNum}`,
    title,
    url: group.url,
  };
}

function FranchiseGroup({ group }: { group: FranchiseJobGroup }) {
  return (
    <div className="flex gap-6 px-4 sm:px-10 lg:px-[72px] 2xl:px-[152px]">
      <Location
        aria-hidden
        className="hidden h-14 w-10 shrink-0 md:block [&_path]:fill-new-beginnings-base"
      />

      <div className="flex flex-1 flex-col gap-8 md:gap-10">
        <div className="flex flex-col gap-2 text-new-beginnings-base">
          <h2 className="font-bold font-jakarta text-xl leading-tight">
            {group.name}
          </h2>

          {group.address ? (
            <div className="flex items-start gap-1 text-sm leading-6 md:gap-0 md:text-base">
              <Location
                aria-hidden
                className="mt-0.5 size-6 shrink-0 md:hidden [&_path]:fill-new-beginnings-base"
              />
              <span>{group.address}</span>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-8">
          {group.kind === "jobs" ? (
            group.jobs.map((job) => <JobCard job={job} key={job.id} />)
          ) : (
            <JobCard
              job={buildFallbackJobItem(group)}
              key={`fallback-${group.franchiseNum}`}
            />
          )}
        </div>

        {group.careersPageUrl ? (
          <div className="border-new-beginnings-tints-600 border-t pt-8">
            <a
              className="inline-flex items-center gap-0.5 font-bold text-base text-new-beginnings-base underline hover:no-underline"
              href={group.careersPageUrl}
            >
              View all jobs for this area
              <ChevronRight aria-hidden className="size-4" />
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { FranchiseGroup };
